import { ReactElement, useMemo } from 'react';
import cc from 'classcat';
import dayjs from 'dayjs';
import { isRtl } from 'utils/constants/app/rtl';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { dateFormat } from 'utils/date-manipulation/date-format';
import {
  getEsportCurrentMapName,
  getGameCurrentState
} from 'utils/sportsbook/game-current-state';
import { shouldShowLiveTime } from 'utils/sportsbook/live-games-helper';
import { Game } from 'interfaces/sportsbook-data-levels';
import './index.less';

type Props = {
  game: Game;
  isLive: boolean;
  timeOnly?: boolean;
  reverse?: boolean;
  currentSport?: string;
  dir?: 'row' | 'col';
  format?: string;
  useGameMapName?: boolean;
  noWrap?: boolean;
};

export const GameTime = ({
  isLive,
  game,
  currentSport,
  timeOnly,
  reverse,
  dir,
  format,
  useGameMapName = false,
  noWrap = false
}: Props): ReactElement => {
  const gameCurrentState = useMemo(() => {
    return useGameMapName && game.info?.additional_data
      ? getEsportCurrentMapName(game.info.additional_data, game.sport_alias)
      : getGameCurrentState(game.info || {}, currentSport);
  }, [game?.id, game?.info]);

  const dateTime = useMemo(() => {
    let dtFormat = `${SpringConfigs.DT.time}${
      timeOnly
        ? ''
        : ` | ${dateFormat({
            date: format || SpringConfigs.DT.longDate,
            includeWeekday: true
          })}`
    }`;

    if (reverse) {
      dtFormat = dtFormat.split(' | ').reverse().join(' | ');
    }

    return dayjs.unix(game.start_ts).format(dtFormat);
  }, [game.start_ts]);

  return (
    <div
      className={cc([
        'gameTime__wrapper',
        {
          'gameTime__wrapper--row': dir === 'row',
          'gameTime__wrapper--reverse': reverse
        }
      ])}
    >
      {isLive && (
        <>
          <span
            className={cc([
              'gameTime__wrapper__liveRedEllipse live__red-ellipse',
              {
                'gameTime__wrapper__liveRedEllipse--withMargin':
                  !!gameCurrentState,
                'gameTime__wrapper__liveRedEllipse--rtl': isRtl,
                empty: !gameCurrentState
              }
            ])}
          />
          {shouldShowLiveTime(isLive, currentSport) && gameCurrentState}
        </>
      )}
      {!isLive && (
        <span
          className={cc([
            'gameTime__wrapper__dateTime date-time',
            {
              'gameTime__wrapper__dateTime--noWrap': noWrap
            }
          ])}
        >
          {dateTime}
        </span>
      )}
    </div>
  );
};
